import React, { useState } from 'react';
import styles from './M_ProfileMenu.module.css';
import A_UserAvatar from '@/components/A_Atoms/A_UserAvatar/A_UserAvatar';
import { User } from '@/types';
import cn from 'classnames';
import A_ProfileMenuSelectButton from '@/components/A_Atoms/A_ProfileMenuSelectButton/A_ProfileMenuSelectButton';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { useIsMobile } from '@/hooks/useIsMobile';

interface M_ProfileMenuProps {
  user: User;
  onTabChange?: (tab: string) => void;
  activeTab?: ProfileTab;
}

type ProfileTab =
  | 'profile'
  | 'security'
  | 'privacy'
  | 'notifications'
  | 'billing'
  | 'wallet';

export default function M_ProfileMenu({
  user,
  onTabChange,
  activeTab: externalActiveTab,
}: M_ProfileMenuProps) {
  const [internalActiveTab, setInternalActiveTab] =
    useState<ProfileTab>('profile');
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isMobile = useIsMobile(1200);

  const activeTab = externalActiveTab || internalActiveTab;

  const balanceLocale =
    language === 'ru' ? 'ru-RU' : language === 'fr' ? 'fr-FR' : 'en-US';

  const formatBalance = (value: number) =>
    `${value.toLocaleString(balanceLocale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} $`;

  const handleTabChange = (tab: ProfileTab) => {
    setInternalActiveTab(tab);
    onTabChange?.(tab);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/auth');
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header_wrapper}>
        <A_UserAvatar
          width={200}
          height={200}
          src={user.avatar}
          alt={user.personal?.firstName || user.handle}
          className={styles.avatar}
        />
        <h4 className={styles.title}>@{user.handle}</h4>
        <div className={styles.profile_metrics}>
          <div className={styles.metric_item}>
            <span className={cn('text-p-7', styles.metric_value)}>
              {user.metrics?.followers ?? 0}
            </span>
            <span className='text-p-7'>
              {getTranslation(language, 'profileMenu.metrics.followers')}
            </span>
          </div>
          <div className={styles.metric_item}>
            <span className={cn('text-p-7', styles.metric_value)}>
              {user.metrics?.following ?? 0}
            </span>
            <span className='text-p-7'>
              {getTranslation(language, 'profileMenu.metrics.following')}
            </span>
          </div>
          <div className={styles.metric_item}>
            <span className={cn('text-p-7', styles.metric_value)}>
              {user.metrics?.coauthors ?? 0}
            </span>
            <span className='text-p-7'>
              {getTranslation(language, 'profileMenu.metrics.coauthors')}
            </span>
          </div>
        </div>
        {isMobile && user.wallet != null && (
          <div className={styles.balance_row}>
            <span className={cn(styles.balance_label, 'text-p-7')}>
              {getTranslation(language, 'capsuleReactions.balanceLabel')}
            </span>
            <span className={cn(styles.balance_value, 'text-p-6')}>
              {formatBalance(user.wallet.balance)}
            </span>
          </div>
        )}
      </div>
      <div className={styles.bottom_wrapper}>
        <div className={styles.tabs_wrapper}>
          <A_ProfileMenuSelectButton
            icon={<Q_Icon.ProfileIcon width={24} height={24} fill='none' />}
            text={getTranslation(language, 'profileMenu.tabs.profile')}
            active={activeTab === 'profile'}
            onClick={() => handleTabChange('profile')}
          />
          <A_ProfileMenuSelectButton
            icon={<Q_Icon.SecurityIcon width={24} height={24} fill='none' />}
            text={getTranslation(language, 'profileMenu.tabs.security')}
            active={activeTab === 'security'}
            onClick={() => handleTabChange('security')}
          />
          <A_ProfileMenuSelectButton
            icon={<Q_Icon.PrivacyIcon width={24} height={24} fill='none' />}
            text={getTranslation(language, 'profileMenu.tabs.privacy')}
            active={activeTab === 'privacy'}
            onClick={() => handleTabChange('privacy')}
          />
          <A_ProfileMenuSelectButton
            icon={
              <Q_Icon.NotificationsIcon width={24} height={24} fill='none' />
            }
            text={getTranslation(language, 'profileMenu.tabs.notifications')}
            active={activeTab === 'notifications'}
            onClick={() => handleTabChange('notifications')}
          />
          <A_ProfileMenuSelectButton
            icon={<Q_Icon.BillingIcon width={24} height={24} fill='none' />}
            text={getTranslation(language, 'profileMenu.tabs.billing')}
            active={activeTab === 'billing'}
            onClick={() => handleTabChange('billing')}
          />
          <A_ProfileMenuSelectButton
            icon={<Q_Icon.BillingIcon width={24} height={24} fill='none' />}
            text={getTranslation(language, 'profileMenu.tabs.wallet')}
            active={activeTab === 'wallet'}
            onClick={() => handleTabChange('wallet')}
            balance={user.wallet?.balance}
          />
        </div>
        <div className={styles.logout_wrapper}>
          <A_ProfileMenuSelectButton
            icon={<Q_Icon.LogoutIcon width={24} height={24} fill='none' />}
            text={getTranslation(language, 'profileMenu.logout')}
            onClick={handleLogout}
          />
        </div>
      </div>
    </div>
  );
}
